import React, { memo } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import type { KPICardProps, KPITrend, KPIVariant, KPISize } from './KPICard.types';

// Icons
const TrendUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 11l4-4 3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 4h5v5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrendDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 5l4 4 3-3 5 6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 12h5v-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrendNeutralIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 8h12" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 5l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Size configurations
const sizeConfig: Record<KPISize, {
  padding: string;
  titleSize: string;
  valueSize: string;
  subtitleSize: string;
  changeSize: string;
  iconSize: string;
  sparklineHeight: number;
}> = {
  sm: {
    padding: 'var(--space-3)',
    titleSize: 'var(--text-xs)',
    valueSize: 'var(--text-lg)',
    subtitleSize: 'var(--text-xs)',
    changeSize: 'var(--text-xs)',
    iconSize: '32px',
    sparklineHeight: 32
  },
  md: {
    padding: 'var(--space-4)',
    titleSize: 'var(--text-sm)',
    valueSize: 'var(--text-2xl)',
    subtitleSize: 'var(--text-xs)',
    changeSize: 'var(--text-sm)',
    iconSize: '40px',
    sparklineHeight: 48
  },
  lg: {
    padding: 'var(--space-6)',
    titleSize: 'var(--text-base)',
    valueSize: 'var(--text-3xl)',
    subtitleSize: 'var(--text-sm)',
    changeSize: 'var(--text-base)',
    iconSize: '48px',
    sparklineHeight: 64
  }
};

// Variant color configurations
const variantColors: Record<KPIVariant, {
  background: string;
  border: string;
  iconBg: string;
  iconColor: string;
}> = {
  default: {
    background: 'var(--color-bg-card)',
    border: 'var(--color-border-default)',
    iconBg: 'var(--color-neutral-100)',
    iconColor: 'var(--color-neutral-600)'
  },
  positive: {
    background: 'var(--color-bg-card)',
    border: 'var(--color-positive-border)',
    iconBg: 'var(--color-positive-bg)',
    iconColor: 'var(--color-positive)'
  },
  negative: {
    background: 'var(--color-bg-card)',
    border: 'var(--color-negative-border)',
    iconBg: 'var(--color-negative-bg)',
    iconColor: 'var(--color-negative)'
  },
  warning: {
    background: 'var(--color-bg-card)',
    border: 'var(--color-warning-500)',
    iconBg: 'var(--color-warning-50)',
    iconColor: 'var(--color-warning-600)'
  },
  primary: {
    background: 'var(--color-bg-card)',
    border: 'var(--color-primary-200)',
    iconBg: 'var(--color-primary-50)',
    iconColor: 'var(--color-primary)'
  }
};

// Trend color helper
const getTrendColor = (trend: KPITrend): string => {
  switch (trend) {
    case 'up':
      return 'var(--color-positive)';
    case 'down':
      return 'var(--color-negative)';
    case 'neutral':
    default:
      return 'var(--color-text-secondary)';
  }
};

// Trend icon helper
const TrendIcon = ({ trend }: { trend: KPITrend }) => {
  switch (trend) {
    case 'up':
      return <TrendUpIcon />;
    case 'down':
      return <TrendDownIcon />;
    case 'neutral':
    default:
      return <TrendNeutralIcon />;
  }
};

// Skeleton component
const Skeleton = ({ width, height, borderRadius = 'var(--radius-sm)' }: {
  width: string;
  height: string;
  borderRadius?: string;
}) => (
  <div
    className="skeleton"
    style={{
      width,
      height,
      borderRadius
    }}
  />
);

function KPICardComponent({
  title,
  value,
  trend,
  change,
  subtitle,
  icon,
  variant = 'default',
  size = 'md',
  loading = false,
  sparklineData,
  showSparkline = false,
  className,
  onClick
}: KPICardProps): React.ReactElement {
  const config = sizeConfig[size];
  const colors = variantColors[variant];
  const isClickable = !!onClick;

  const sparklineColor = trend === 'down'
    ? 'var(--color-negative)'
    : trend === 'up'
      ? 'var(--color-positive)'
      : 'var(--color-primary)';

  return (
    <div
      className={className}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: config.padding,
        background: colors.background,
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${colors.border}`,
        boxShadow: 'var(--shadow-sm)',
        transition: 'all var(--transition-fast)',
        cursor: isClickable ? 'pointer' : 'default',
        ...(isClickable && {
          ':hover': {
            boxShadow: 'var(--shadow-md)',
            borderColor: 'var(--color-primary-300)'
          }
        })
      }}
    >
      {/* Header: Title and Icon */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 'var(--space-3)'
      }}>
        <div style={{ flex: 1 }}>
          {loading ? (
            <Skeleton width="60%" height={config.titleSize} />
          ) : (
            <span style={{
              fontSize: config.titleSize,
              fontWeight: 500,
              color: 'var(--color-text-secondary)',
              letterSpacing: 'var(--tracking-wide)'
            }}>
              {title}
            </span>
          )}
        </div>

        {icon && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: config.iconSize,
            height: config.iconSize,
            borderRadius: 'var(--radius-md)',
            background: colors.iconBg,
            color: colors.iconColor,
            flexShrink: 0
          }}>
            {icon}
          </div>
        )}
      </div>

      {/* Value */}
      <div style={{ marginBottom: 'var(--space-2)' }}>
        {loading ? (
          <Skeleton width="80%" height={config.valueSize} />
        ) : (
          <span style={{
            fontSize: config.valueSize,
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--color-text-primary)',
            lineHeight: 1.2
          }}>
            {value}
          </span>
        )}
      </div>

      {/* Trend and Change */}
      {(trend || change !== undefined) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: subtitle ? 'var(--space-2)' : 0
        }}>
          {loading ? (
            <Skeleton width="40%" height={config.changeSize} />
          ) : (
            <>
              {trend && (
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: getTrendColor(trend)
                }}>
                  <TrendIcon trend={trend} />
                </span>
              )}
              {change !== undefined && (
                <span style={{
                  fontSize: config.changeSize,
                  fontWeight: 600,
                  fontFamily: 'var(--font-mono)',
                  color: trend ? getTrendColor(trend) : 'var(--color-text-secondary)'
                }}>
                  {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Subtitle */}
      {subtitle && (
        <div>
          {loading ? (
            <Skeleton width="50%" height={config.subtitleSize} />
          ) : (
            <span style={{
              fontSize: config.subtitleSize,
              color: 'var(--color-text-tertiary)'
            }}>
              {subtitle}
            </span>
          )}
        </div>
      )}

      {/* Sparkline */}
      {showSparkline && sparklineData && sparklineData.length > 0 && (
        <div style={{
          marginTop: 'var(--space-3)',
          height: config.sparklineHeight,
          marginLeft: `calc(-1 * ${config.padding})`,
          marginRight: `calc(-1 * ${config.padding})`,
          marginBottom: `calc(-1 * ${config.padding})`,
          borderBottomLeftRadius: 'var(--radius-lg)',
          borderBottomRightRadius: 'var(--radius-lg)',
          overflow: 'hidden'
        }}>
          {loading ? (
            <Skeleton width="100%" height={`${config.sparklineHeight}px`} borderRadius="0" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparklineData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`sparkline-gradient-${title.replace(/\s/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={sparklineColor} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={sparklineColor} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={sparklineColor}
                  strokeWidth={2}
                  fill={`url(#sparkline-gradient-${title.replace(/\s/g, '-')})`}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}

export const KPICard = memo(KPICardComponent);
